import { Component, OnInit } from '@angular/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { NgxCsvParser, NgxCSVParserError } from 'ngx-csv-parser';
import { ConfigurationConstants, ProcCoordPointTypeEnum } from 'src/app/models/EnumTypes';
import { ImportFormat } from 'src/app/models/importFormat';
import { ProcedureCSVContent, ProcedureCsvModel } from 'src/app/models/procedure';
import { ModalControlService } from 'src/app/services/modal-control.service';
import { ProcedureControlService } from 'src/app/services/procedure-control.service';
import { TabsService } from 'src/app/services/tabsService';

@Component({
  selector: 'app-procedures-csv',
  templateUrl: './procedures-csv.component.html',
  styleUrls: ['../../../app.component.scss'],
  standalone: false
})
export class ProceduresCsvComponent implements OnInit {

  public procedureImportCSVModel = new ProcedureCsvModel();

  importFormat: ImportFormat = ImportFormat.CSV_FORMAT;

  constructor(private bsModalRef: BsModalRef,
    private modalService: ModalControlService,
    private ngxCsvParser: NgxCsvParser,
    public procedureService: ProcedureControlService,
    private tabsService: TabsService
  ) {
    this.procedureImportCSVModel = new ProcedureCsvModel();
  }

  ngOnInit(): void {
    this.procedureImportCSVModel.fillTable();
  }

  cancelImport(): void {
    this.bsModalRef.hide();
  }

  saveFile(event: any): void {
    this.procedureImportCSVModel.file = event.target.files[0];
  }

  startImport(): void {
    if (this.procedureImportCSVModel.file == null) {
      this.modalService.showMesssageModal("Error", "You have to choose a file before import it.");
      return;
    }
    if (!this.ngxCsvParser.isCSVFile(this.procedureImportCSVModel.file)) {
      this.modalService.showMesssageModal("Error", 'The inserted file has an invalid extension. Insert a "csv" file.');
      return;
    }
    if (this.procedureImportCSVModel.decimalSeparator == this.procedureImportCSVModel.fieldSeparator) {
      this.modalService.showMesssageModal("Error", 'Please define a different field and decimal separator.');
      return;
    }
    this.ngxCsvParser
      .parse(this.procedureImportCSVModel.file, { header: false, delimiter: this.procedureImportCSVModel.fieldSeparator })
      .subscribe((result: Array<any>) => {
        for (let i = this.procedureImportCSVModel.dataStartingLine - 1; i < result.length; i++) {
          if (result[i].length == 6) {
            // The system was able to parse successfully the CSV file.
            this.procedureImportCSVModel.fileContent.push(ProcedureCSVContent.map(result[i], this.procedureImportCSVModel.table));
          } else {
            // The system was not able to parse successfully the CSV file.
            this.modalService.showMesssageModal("Import Procedure", "Error encountered in line " + (i + 1) + ".");
            return;
          }

        }
        let errors: string[] = this.validateContent(this.procedureImportCSVModel.fileContent);
        if (errors.length == 0) {
          this.tabsService.openNewCreateTab(TabsService.CREATE_PROCEDURE_FROM_CSV, this.procedureImportCSVModel);
        } else {
          this.modalService.showMultipleMesssagesModal('Validation Error', errors);
        }

      }, (error: NgxCSVParserError) => {
        this.modalService.showMesssageModal("Error", "Unable to extract information in CSV.");
      },
        () => {
          this.bsModalRef.hide();
        });
  }

  validateContent(fileContent: ProcedureCSVContent[]): string[] {
    let errors: string[] = [];
    let currentIdentifier: string;
    let errorMessage: string;
    fileContent.forEach((line, index) => {
      if (!this.stringNullOrEmpty(line.coordinatePointId)) {
        currentIdentifier = line.coordinatePointId;
      } else {
        currentIdentifier = "CoordPoint";
      }

      if (this.stringNullOrEmpty(line.coordinatePointId)) {
        errorMessage = "Property ID cannot be null or empty";
        errors.push(this.buildErrorMessage(currentIdentifier, errorMessage, index));
      } else if (!this.isValidCoordPointId(line.coordinatePointId)) {
        errorMessage = "Property ID cannot exceed 5 characters";
        errors.push(this.buildErrorMessage(currentIdentifier, errorMessage, index));
      }

      if (this.stringNullOrEmpty(line.sequenceNumber)) {
        errorMessage = "Property Sequence Number cannot be null or empty";
        errors.push(this.buildErrorMessage(currentIdentifier, errorMessage, index));
      } else if (!this.isValidSequenceNumber(line.sequenceNumber)) {
        errorMessage = "Property Sequence Number is not a valid integer";
        errors.push(this.buildErrorMessage(currentIdentifier, errorMessage, index));
      }

      if (this.stringNullOrEmpty(line.wtpType)) {
        errorMessage = "Property WP type cannot be null or empty";
        errors.push(this.buildErrorMessage(currentIdentifier, errorMessage, index));
      } else if (!this.isValidProcCoordType(line.wtpType)) {
        errorMessage = "Property WP type: '" + line.wtpType + "' is not allowed";
        errors.push(this.buildErrorMessage(currentIdentifier, errorMessage, index));
      }

      if (this.stringNullOrEmpty(line.latitude)) {
        errorMessage = "Property Latitude cannot be null or empty";
        errors.push(this.buildErrorMessage(currentIdentifier, errorMessage, index));
      } else if (!this.isValidLat(line.latitude)) {
        errorMessage = "Property Latitude is not a valid number";
        errors.push(this.buildErrorMessage(currentIdentifier, errorMessage, index));
      }

      if (this.stringNullOrEmpty(line.longitude)) {
        errorMessage = "Property Longitude cannot be null or empty";
        errors.push(this.buildErrorMessage(currentIdentifier, errorMessage, index));
      } else if (!this.isValidLon(line.longitude)) {
        errorMessage = "Property Longitude is not a valid number";
        errors.push(this.buildErrorMessage(currentIdentifier, errorMessage, index));
      }

      if (this.stringNullOrEmpty(line.height)) {
        errorMessage = "Property Altitude cannot be null or empty";
        errors.push(this.buildErrorMessage(currentIdentifier, errorMessage, index));
      } else if (!this.isValidAltitude(line.height)) {
        errorMessage = "Property Altitude is not a valid number";
        errors.push(this.buildErrorMessage(currentIdentifier, errorMessage, index));
      }
    });
    return errors;
  }

  private buildErrorMessage(identifier: string, errorMessage: string, index: number): string {
    return `CoordPoint: ${identifier} - ${errorMessage}. Line ${(index + this.procedureImportCSVModel.dataStartingLine)}.\n`;
  }

  private stringNullOrEmpty(string: string): boolean {
    if (string) {
      return false;
    } else {
      return true;
    }
  }

  private isValidCoordPointId(coordPointId: string): boolean {
    if (coordPointId.length >= ConfigurationConstants.COORDINATE_POINT_ID_LENGTH_MIN
      && coordPointId.length <= ConfigurationConstants.COORDINATE_POINT_ID_LENGTH_MAX) {
      return true;
    } else {
      return false;
    }
  }

  private isValidSequenceNumber(seqNumber: string): boolean {
    let sequenceNumber: Number = Number(seqNumber);
    if (Number.isNaN(sequenceNumber)) {
      return false;
    }
    return Number.isInteger(sequenceNumber);
  }

  private isValidAltitude(altitude: string): boolean {
    try {
      let altitudeNumber: Number = Number(altitude);
      if (Number.isNaN(altitudeNumber)) {
        return false;
      }
    } catch (e) {
      return false;
    }
    return true;
  }

  private isValidProcCoordType(procCoordPType: string): boolean {
    try {
      let key = Object.keys(ProcCoordPointTypeEnum).find(x => ProcCoordPointTypeEnum[x] == procCoordPType);
      if (key == null) {
        return false;
      }
    } catch (e) {
      return false;
    }
    return true;
  }

  private isValidLat(lat: string): boolean {
    try {
      let latDouble: number = Number(lat);
      if (Number.isNaN(latDouble)) {
        return false;
      }
      latDouble = Number.parseFloat(lat);
      if (latDouble <= ConfigurationConstants.LATITUDE_MAX && latDouble >= ConfigurationConstants.LATITUDE_MIN) {
        return true;
      } else {
        return false;
      }
    } catch (e) {
      return false;
    }
  }

  private isValidLon(lon: string): boolean {
    try {
      let lonDouble: number = Number(lon);
      if (Number.isNaN(lonDouble)) {
        return false;
      }
      lonDouble = Number.parseFloat(lon);
      if (lonDouble <= ConfigurationConstants.LONGITUDE_MAX && lonDouble >= ConfigurationConstants.LONGITUDE_MIN) {
        return true;
      } else {
        return false;
      }
    } catch (e) {
      return false;
    }
  }
}
